import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { IProductivityTimeframe } from '@interfaces/IProductivity';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { APP_DEFAULT_TZ } from '@utils/dateUtils';
import { writeLastSiteId } from '@utils/lastSite';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { PageActions } from '@components/ui/PageFrame/PageFrame';
import { ExportButton } from './ExportButton/ExportButton';
import { Card } from './shared/Card/Card';
import { TimeframeSelector } from './Cards/TimeframeSelector/TimeframeSelector';
import { MetricStrip } from './Cards/MetricStrip/MetricStrip';
import { CoverageBar } from './Cards/MeasurementCoverage/CoverageBar';
import { isUnconfigured } from './Cards/MeasurementCoverage/coverageLines';
import { ExpectedVsActual } from './Cards/ExpectedVsActual/ExpectedVsActual';
import { ConfidenceMix } from './Cards/ConfidenceMix/ConfidenceMix';
import { Breakdown } from './Cards/Breakdown/Breakdown';
import { ExceptionsCenter } from './Cards/ExceptionsCenter/ExceptionsCenter';
import { useProductivitySummary } from './hooks/useProductivitySummary';
import { useProductivityPeriod } from './hooks/useProductivityPeriod';
import { useProductivityView } from './hooks/useProductivityView';
import { customRangeFor, granularityForTimeframe, resolveProductivityRange } from './utils/productivityRange';
import { MetricStripSkeleton } from './Cards/MetricStrip/MetricStripSkeleton';
import { ExpectedVsActualSkeleton } from './Cards/ExpectedVsActual/ExpectedVsActualSkeleton';
import { ConfidenceMixSkeleton } from './Cards/ConfidenceMix/ConfidenceMixSkeleton';
import { ExceptionsSkeleton } from './Cards/ExceptionsCenter/ExceptionsSkeleton';
import styles from './ProductivityPage.module.css';

export function ProductivityPage() {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();

  useEffect(() => {
    if (siteId) writeLastSiteId(siteId);
  }, [siteId]);

  // Decides which calendar day the rolling windows end on.
  const tz = useSelector(selectSiteTz) ?? APP_DEFAULT_TZ;

  // Timeframe, custom dates and the open tab live in the URL.
  const { timeframe, customRange, tab, setView } = useProductivityView(tz);

  const range = useMemo(
    () => resolveProductivityRange(timeframe, customRange, tz),
    [timeframe, customRange, tz],
  );
  const granularity = granularityForTimeframe(timeframe, range);

  // Custom opens on the dates already shown: same range, same query key, no
  // request, and no empty state to click out of.
  const pickTimeframe = (next: IProductivityTimeframe) => {
    if (next === 'custom' && range) setView({ timeframe: next, customRange: customRangeFor(range) });
    else setView({ timeframe: next });
  };

  const summary = useProductivitySummary(siteId);
  const summaryPending = !summary.data && !summary.isError;

  // One request per timeframe: chart, confidence mix and the open tab arrive together.
  const period = useProductivityPeriod(siteId, range, granularity, tab);
  // Across a timeframe change the previous payload stands in: the cards keep
  // it, dimmed. The breakdown waits whenever the period is on the wire — first
  // load, new timeframe, or a stale refresh — since its response re-seeds the
  // open tab, so the tab never asks on its own for rows already on the way.

  const coverage = summary.data?.coverage;

  // The site has a plan and none of it is measurable. Every surface below would
  // be an empty shell.
  const unmeasured = coverage ? isUnconfigured(coverage) : false;

  return (
    <div className={styles.page}>
      <PageActions>
        <ExportButton summary={summary.data} />
      </PageActions>
      {summary.isError ? (
        <DataUnavailable message={t('productivity.error')} />
      ) : (
        <>
          <CoverageBar coverage={coverage} siteId={siteId} />

          {!unmeasured && (
            <>
              {summaryPending ? <MetricStripSkeleton /> : <MetricStrip metrics={summary.data.metrics} />}
              {/* The Spatial Insight card is hidden until a real insight exists —
                  the payload still carries a fixture (follow-ups §1). The card,
                  its skeleton and the field stay; this is the only line to restore. */}
              {summaryPending ? <ExceptionsSkeleton /> : <ExceptionsCenter exceptions={summary.data.exceptions} />}
            </>
          )}
        </>
      )}

      {/* The timeframe surfaces carry their own queries, so a failed SUMMARY
          must not blank them. They drop out only for a site with nothing
          measurable, where they would have nothing to show either. */}
      {!unmeasured && (
        <>
        <TimeframeSelector
              value={timeframe}
              onChange={pickTimeframe}
              customRange={customRange}
              onCustomRangeChange={(customRange) => setView({ customRange })}
            />

            <div className={styles.lowerGrid}>
              {!range ? (
                <Card>
                  <DataUnavailable message={t('productivity.selectTimeframe')} />
                </Card>
              ) : period.isError ? (
                <Card>
                  <DataUnavailable message={t('productivity.error')} />
                </Card>
              ) : period.data ? (
                <ExpectedVsActual data={period.data.earnedVsActual} refreshing={period.isPlaceholderData} />
              ) : (
                <ExpectedVsActualSkeleton />
              )}
              {!range ? (
                <Card>
                  <DataUnavailable message={t('productivity.selectTimeframe')} />
                </Card>
              ) : period.isError ? (
                <Card>
                  <DataUnavailable message={t('productivity.error')} />
                </Card>
              ) : period.data ? (
                <ConfidenceMix bands={period.data.confidenceMix} refreshing={period.isPlaceholderData} />
              ) : (
                <ConfidenceMixSkeleton />
              )}
            </div>

          <Breakdown siteId={siteId} range={range} active={tab} onSelect={(tab) => setView({ tab })} periodFetching={period.isFetching} />
        </>
      )}
    </div>
  );
}